import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  YOUTUBE_CALLBACK_PARAM,
  YOUTUBE_CALLBACK_RESULTS,
  type YouTubeCallbackResult,
} from '@liveforge/shared';
import { useYouTubeConnection } from '../hooks/use-youtube-connection';
import { Button } from './ui/button';
import { ConfirmDialog } from './ui/confirm-dialog';
import { FormError } from './ui/field';
import { InlineLoader } from './ui/spinner';

/**
 * The YouTube channel bound to a Show.
 *
 * A card rather than a form field: what the Show owns is a *grant*, not a
 * string. The channel id used to be typed in by hand, which could name a
 * channel nobody here can broadcast to; it is now written by the consent flow
 * and shown read-only.
 */

const RESULT_MESSAGE: Record<YouTubeCallbackResult, { tone: string; text: string }> = {
  connected: { tone: 'alert--info', text: 'YouTube channel connected.' },
  denied: {
    tone: 'alert--warning',
    text: 'Authorisation was cancelled, so nothing was connected.',
  },
  failed: {
    tone: 'alert--error',
    text: 'YouTube could not be connected. Check the server logs and try again.',
  },
};

function isCallbackResult(value: string | null): value is YouTubeCallbackResult {
  return value !== null && (YOUTUBE_CALLBACK_RESULTS as readonly string[]).includes(value);
}

export interface YouTubeCardProps {
  showId: string;
}

export function YouTubeCard({ showId }: YouTubeCardProps) {
  const { status, loading, error, busy, connect, disconnect } = useYouTubeConnection(showId);
  const [, setSearchParams] = useSearchParams();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  /*
   * The OAuth round trip reports itself through the query string, because that
   * is all a redirect can carry.
   *
   * Read once, at mount, rather than mirrored from the URL into state by an
   * effect: the consent flow is a full page load, so the parameter is always
   * there on the first render, and reading it this way leaves nothing to
   * re-synchronise. Nothing has to be reloaded either - the callback wrote the
   * connection before redirecting, so the hook's own first fetch already sees
   * it.
   */
  const [outcome] = useState<YouTubeCallbackResult | null>(() => {
    const value = new URLSearchParams(window.location.search).get(YOUTUBE_CALLBACK_PARAM);
    return isCallbackResult(value) ? value : null;
  });

  // Then take it out of the URL: a refresh, or a link someone pasted, must not
  // keep announcing a connection that happened minutes ago.
  useEffect(() => {
    if (!outcome) {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(YOUTUBE_CALLBACK_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [outcome, setSearchParams]);

  const connection = status?.connection ?? null;
  const configured = status?.configured ?? false;

  return (
    <section className="card">
      <div className="card__header">
        <h2>YouTube</h2>
        {connection ? <span className="badge badge--live">Connected</span> : null}
      </div>

      {outcome ? (
        <p className={`alert ${RESULT_MESSAGE[outcome].tone}`}>{RESULT_MESSAGE[outcome].text}</p>
      ) : null}

      <FormError>{error}</FormError>

      {loading ? (
        <InlineLoader label="Checking the connection..." />
      ) : !configured ? (
        // A deployment without a Google OAuth client. Saying so beats offering
        // a button whose only possible outcome is an error.
        <p className="muted">
          This server has no Google OAuth client configured, so no channel can be connected. Set{' '}
          <code className="mono">YOUTUBE_CLIENT_ID</code>,{' '}
          <code className="mono">YOUTUBE_CLIENT_SECRET</code>,{' '}
          <code className="mono">YOUTUBE_REDIRECT_URI</code> and{' '}
          <code className="mono">YOUTUBE_TOKEN_KEY</code> on the backend.
        </p>
      ) : connection ? (
        <div className="stack">
          <div className="youtube-channel">
            {connection.channel.thumbnailUrl ? (
              <img
                className="youtube-channel__avatar"
                src={connection.channel.thumbnailUrl}
                alt=""
                width={48}
                height={48}
              />
            ) : (
              <div className="youtube-channel__avatar youtube-channel__avatar--empty" />
            )}
            <div className="youtube-channel__text">
              <a
                className="youtube-channel__title"
                href={`https://www.youtube.com/channel/${connection.channel.id}`}
                target="_blank"
                rel="noreferrer"
              >
                {connection.channel.title}
              </a>
              <span className="mono muted youtube-channel__id">
                {connection.channel.customUrl ?? connection.channel.id}
              </span>
            </div>
          </div>

          <p className="muted">
            Lives created from this show will broadcast here. LiveForge holds an offline
            authorisation for this channel and refreshes it on its own.
          </p>

          <div className="form-actions">
            <Button variant="ghost" small onClick={() => setConfirmingDisconnect(true)}>
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="muted">
            Connect the channel this show broadcasts to. LiveForge will be able to create
            broadcasts, read their stream key and follow the live chat.
          </p>
          <div className="form-actions">
            <Button variant="primary" loading={busy} onClick={() => void connect()}>
              Connect YouTube
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmingDisconnect}
        title="Disconnect YouTube"
        message="LiveForge will forget this channel and revoke its access. Lives already created keep their broadcast on YouTube."
        confirmLabel="Disconnect"
        busy={busy}
        onConfirm={() => {
          void disconnect().then(() => setConfirmingDisconnect(false));
        }}
        onCancel={() => setConfirmingDisconnect(false)}
      />
    </section>
  );
}
